import { Button, CopyButton, Image, Modal, TextInput } from '@mantine/core'
import { Typography } from '../common/typography'
import classes from './Settings.module.css'
import { zodResolver } from 'mantine-form-zod-resolver'
import { useForm } from '@mantine/form'
import {
  Otp,
  TwoFactorAuthentication,
  TwoFactorAuthenticationSchema,
  useEnableOtpVerify,
} from './api'
import { useMediaQuery } from '@mantine/hooks'

export default function EnableTwoFactorAuthentication({
  openedEnableModal,
  closeEnableModal,
  otp_device_name,
  resetEnableOtp,
  dataEnable,
}: {
  openedEnableModal: boolean
  closeEnableModal: () => void
  otp_device_name: string
  resetEnableOtp: () => void
  dataEnable: Otp
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const { mutate: verifyOtp, isPending: isVerifyOtpPending } =
    useEnableOtpVerify()

  const twoFactorAuthenticationForm = useForm<TwoFactorAuthentication>({
    initialValues: {
      token: '',
    },
    validate: zodResolver(TwoFactorAuthenticationSchema),
  })

  const handleEnableOtpVerify = (values: TwoFactorAuthentication) => {
    verifyOtp(values, {
      onSuccess: () => {
        resetEnableOtp()
        closeEnableModal()
      },
    })
  }

  return (
    <Modal.Root
      opened={openedEnableModal}
      onClose={closeEnableModal}
      centered
      classNames={{
        header: classes.twoFactorAuthenticationModalHeader,
        body: classes.twoFactorAuthenticationModalBody,
      }}
      transitionProps={{
        transition: 'fade',
        duration: 200,
        exitDuration: 150,
        timingFunction: 'ease-out',
      }}
    >
      <Modal.Overlay backgroundOpacity={0.3} blur={2} />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            <Typography fw={500} lh="125">
              Two Factor Authentication
            </Typography>
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <div className={classes.otpDeviceNameContainer}>
            <Typography fs="sm" fw={600} tt="capitalize" lh="140">
              Your device token:
            </Typography>
            <Typography fs="sm" lh="140">
              {otp_device_name}
            </Typography>
          </div>
          <form
            className={classes.twoFactorAuthenticationForm}
            onSubmit={twoFactorAuthenticationForm.onSubmit(
              handleEnableOtpVerify
            )}
          >
            <Typography fs="sm" lh="140">
              Add this token to Google Authenticator App by copying the Time
              based secret key.
            </Typography>
            {isDesktop && (
              <div className={classes.qrCodeContainer}>
                <Image
                  src={`data:image/png;base64,${dataEnable.qr_base64}`}
                  alt="2FA QR code"
                  className={classes.qrCode}
                  style={{ width: '200px', height: '200px' }}
                  fit="scale-down"
                />
              </div>
            )}
            <CopyButton value={dataEnable.secret}>
              {({ copied, copy }) => (
                <Button
                  className={classes.twoFactorAuthenticationButton}
                  onClick={copy}
                >
                  <Typography
                    fs="sm"
                    fw={500}
                    fc={{ light: 'white', dark: 'white' }}
                    lh="100"
                    tt="uppercase"
                  >
                    {copied ? 'Copied' : 'Copy key'}
                  </Typography>
                </Button>
              )}
            </CopyButton>
            <TextInput
              className={classes.inputContainer}
              label="Add Your OTP Device"
              placeholder="Enter Your OTP Token"
              {...twoFactorAuthenticationForm.getInputProps('token')}
            />
            <Button
              className={classes.twoFactorAuthenticationButton}
              disabled={
                twoFactorAuthenticationForm.getInputProps('token').value === ''
              }
              loading={isVerifyOtpPending}
              type="submit"
            >
              <Typography
                fs="sm"
                fw={500}
                fc={{ light: 'white', dark: 'white' }}
                lh="100"
                tt="uppercase"
              >
                Confirm two factor device
              </Typography>
            </Button>
            <Typography fs="sm" lh="140">
              Make sure to confirm your OTP Device by entering the Token from
              Google Authenticator App. Otherwise You will be locked out from
              Your account.
            </Typography>
          </form>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  )
}
